import type { TelemetryEvent } from './events';

/**
 * Ensures `window.dataLayer` exists without ever overwriting an existing instance.
 *
 * Google's guidance: initialize `window.dataLayer = window.dataLayer || []` once per
 * page, keep exactly one dataLayer instance, and only ever mutate it via `.push()`.
 * Re-assigning `window.dataLayer` after GTM has loaded discards queued events.
 *
 * @see https://developers.google.com/tag-platform/tag-manager/datalayer
 * @returns the singleton dataLayer array, or `undefined` when `window` is unavailable.
 */
export function ensureDataLayer(): unknown[] | undefined {
  if (typeof window === 'undefined') return undefined;
  if (!window.dataLayer) {
    window.dataLayer = [];
  }
  return window.dataLayer;
}

/**
 * Pushes a normalized telemetry event onto the dataLayer.
 *
 * Safely no-ops when `window`/`dataLayer` is unavailable (SSR, tests) rather than
 * throwing. Never re-assigns the dataLayer; only mutates via `.push()`.
 *
 * @see https://developers.google.com/tag-platform/tag-manager/datalayer
 */
export function pushEvent(event: TelemetryEvent): void {
  const dataLayer = ensureDataLayer();
  if (!dataLayer) return;
  dataLayer.push(event);
}
